import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useCompanyProfile, useUpdateCompanyProfile, useUploadCompanyLogo } from "@/hooks/useCompanyProfile";
import { toast } from "sonner";
import { Loader2, Upload, Building2, X } from "lucide-react";

export const CompanyProfileTab = () => {
  const { data: profile, isLoading } = useCompanyProfile();
  const updateProfile = useUpdateCompanyProfile();
  const uploadLogo = useUploadCompanyLogo();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [footerText, setFooterText] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setName(profile.name ?? "");
      setLegalName(profile.legal_name ?? "");
      setAddress(profile.address ?? "");
      setCity(profile.city ?? "");
      setState(profile.state ?? "");
      setZip(profile.zip ?? "");
      setPhone(profile.phone ?? "");
      setWebsite(profile.website ?? "");
      setFooterText(profile.footer_text ?? "");
      setPreviewUrl(profile.logo_url);
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        name: name.trim() || undefined,
        legal_name: legalName.trim() || null,
        address: address.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        zip: zip.trim() || null,
        phone: phone.trim() || null,
        website: website.trim() || null,
        footer_text: footerText.trim() || null,
      });
      toast.success("Company profile saved");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2MB");
      return;
    }
    try {
      const url = await uploadLogo.mutateAsync(file);
      setPreviewUrl(url);
      toast.success("Logo uploaded");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" /> Company Profile
        </CardTitle>
        <CardDescription>
          Your company name, logo, and contact info appear on work orders, estimates, and other documents.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo */}
        <div className="space-y-2">
          <Label>Company Logo</Label>
          <div className="flex items-center gap-4">
            {previewUrl ? (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Company logo"
                  className="h-16 w-auto max-w-[200px] object-contain rounded border bg-background p-1"
                />
                <button
                  onClick={() => setPreviewUrl(null)}
                  className="absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="h-16 w-32 rounded border border-dashed flex items-center justify-center text-muted-foreground text-xs">
                No logo
              </div>
            )}
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploadLogo.isPending}>
                {uploadLogo.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Upload Logo
              </Button>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG, or SVG. Max 2MB.</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Company Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Company Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Company" />
          </div>
          <div>
            <Label>Legal Name</Label>
            <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="e.g. Your Company LLC" />
            <p className="text-xs text-muted-foreground mt-1">Used on contracts and legal documents</p>
          </div>
        </div>

        <Separator />

        {/* Address */}
        <div className="space-y-4">
          <Label className="text-sm font-semibold">Address</Label>
          <div>
            <Label className="text-xs">Street Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
            </div>
            <div>
              <Label className="text-xs">State</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="TX" />
            </div>
            <div>
              <Label className="text-xs">ZIP</Label>
              <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="12345" />
            </div>
          </div>
        </div>

        <Separator />

        {/* Contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourcompany.com" />
          </div>
        </div>

        <Separator />

        {/* Footer */}
        <div className="max-w-lg">
          <Label>Document Footer Text</Label>
          <Textarea
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            placeholder="e.g. Your Company LLC · Texas"
            rows={2}
          />
          <p className="text-xs text-muted-foreground mt-1">Appears at the bottom of generated documents (WOs, estimates, etc.)</p>
        </div>

        <Button onClick={handleSave} disabled={updateProfile.isPending}>
          {updateProfile.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
          Save Profile
        </Button>
      </CardContent>
    </Card>
  );
};
